/**
 * EthicsGlobal Widget - Minimal Redux Store
 *
 * Isolated store for the embeddable widget, containing only the necessary
 * slices without dependencies on the main application store.
 */

import { configureStore, createSlice } from '@reduxjs/toolkit';

// Storage keys with namespace to avoid conflicts
const STORAGE_KEYS = {
  lang: 'eg_lang',
  domain: 'eg_domain',
  theme: 'eg_theme'
};

// Minimal app slice for widget
const widgetAppSlice = createSlice({
  name: 'app',
  initialState: {
    campaingName: "",
    channels: {
      website: true,
      phones: {
        isActive: false,
        listPhones: []
      },
      chat_app: {
        text: "",
        is_active: false,
      },
      email: {
        email: "",
        is_active: false,
      },
      whatsapp: {
        is_active: false,
        phone_number: "",
      },
    },
    content: {
      grievanceBtn: null,
      trackBtn: null,
      welcomeMsg: "",
      howFunction: {},
      questions: [],
      formTitle: "",
      formMsg: "",
      trackingGrievanceTitle: "",
      trackingGrievanceMsg: ""
    },
    colors: {
      primary_color: "#009ed7",
      secondary_color: "#546e7a",
      grievance_btn_color: "#f5a888",
      track_btn_color: "#f5a623",
      track_btn_hover_color: "#f5a623",
      primary_text_color: "#ffffff",
      secondary_text_color: "#ffffff",
    },
    companyLogo: "",
    campaingLogo: "",
    backgroud: "",
    qr: "",
    support_material: [{ file: "" }],
    full_form: false,
    origin: null,
    domain: null,
    formParams: null,
    modal_section: false,
    formType: "page",
    innerWidth: 0,
    innerHeight: 0,
    size: "xs",
    lang: null,
    multilanguage: false,
    aviable_langs: ['es', 'en', 'pt', 'fr'],
    external_domain: null,
  },
  reducers: {
    setAppInfo: (state, { payload }) => {
      state.origin = payload.origin;
      state.domain = payload.domain;
      state.campaingName = payload.campaing_name;
      state.channels = payload.channels;
      state.content = payload.content;
      state.colors = payload.colors;
      state.companyLogo = payload.company_logo;
      state.campaingLogo = payload.campaign_logo;
      state.backgroud = payload.backgroud;
      state.qr = payload.qr;
      state.full_form = payload.full_form;
      state.multilanguage = payload.multilanguage;
      state.aviable_langs = payload.aviable_langs;
      state.hub = payload.hub;
      state.modal_section = payload.modal_section;
      state.external_domain = payload.external_domain ?? null;

      if (payload.support_material !== null && payload.support_material !== undefined) {
        state.support_material = payload.support_material;
      }
    },
    setFormParams: (state, { payload }) => {
      state.formParams = payload;
    },
    setBrowserSize: (state, { payload }) => {
      state.innerWidth = payload.innerWidth;
      state.innerHeight = payload.innerHeight;
      state.size = payload.size;
    },
    setLang: (state, { payload }) => {
      state.lang = payload;
      // Store in namespaced localStorage
      try {
        localStorage.setItem(STORAGE_KEYS.lang, payload);
      } catch (e) {
        // localStorage not available
      }
    },
    setDomain: (state, { payload }) => {
      state.domain = payload;
      state.origin = payload;
    }
  }
});

// Minimal theme slice for widget
const widgetThemeSlice = createSlice({
  name: 'theme',
  initialState: {
    gTheme: 'light',
    colors: {
      ethicsBlue: "#009ED7",
      blueGray: "#BDCAD0",
      oxfordBlue: "#152235",
      white: "#FFFFFF",
      blueGray50: "#f3f7fa",
      blueGray100: "#e6ebef",
      blueGray200: "#d3dbe1",
      blueGray500: "#b5c0c8",
      blueGray600: "#909ca4",
      blueGray700: "#5c666e",
      blueGray800: "#262f38",
      raisinBlack: "#0D0C11",
      dark: "#141319",
      dark700: "#2C2C3A",
      dark800: "#242836",
      dark900: "#1a1d27",
      charcoalGrey: "#383941",
      charcoalGrey600: "#32384D",
      blue: "#3181ff",
      green: "#44cb67",
      yellow: "#ffaf00",
      red: "#de463d",
      teal: "#007b6f",
      purple: "#7c4dff",
      pink: "#d81b60",
      lime: "#cddc39",
    }
  },
  reducers: {
    setTheme: (state, { payload }) => {
      state.gTheme = payload;
      try {
        localStorage.setItem(STORAGE_KEYS.theme, payload);
      } catch (e) {
        // localStorage not available
      }
    }
  }
});

// Export actions
export const { setAppInfo, setFormParams, setBrowserSize, setLang, setDomain } = widgetAppSlice.actions;
export const { setTheme } = widgetThemeSlice.actions;

// Create store factory function to allow multiple widget instances
export const createWidgetStore = (initialState = {}) => {
  return configureStore({
    reducer: {
      theme: widgetThemeSlice.reducer,
      app: widgetAppSlice.reducer,
    },
    preloadedState: initialState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  });
};

// Default store instance for single widget use
export const widgetStore = createWidgetStore();

// Storage key exports for external use
export { STORAGE_KEYS };

export default widgetStore;
